import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type NotificationPriority = 'critical' | 'high' | 'normal' | 'low';

type QueueItem = {
  id: string;
  priority: NotificationPriority;
  createdAt: number;
  activate: () => void;
};

type EnqueueInput = {
  id: string;
  priority?: NotificationPriority;
  activate: () => void;
};

interface NotificationQueueContextType {
  enqueue: (item: EnqueueInput) => void;
  release: (id: string) => void;
  cancel: (id: string) => void;
  isActive: (id: string) => boolean;
  activeId: string | null;
  activePriority: NotificationPriority | null;
  hasBlocking: boolean;
}

const PRIORITY_RANK: Record<NotificationPriority, number> = {
  critical: 4,
  high: 3,
  normal: 2,
  low: 1,
};

const NotificationQueueContext = createContext<NotificationQueueContextType | undefined>(undefined);

export function NotificationQueueProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [active, setActive] = useState<QueueItem | null>(null);

  const enqueue = useCallback(
    ({ id, priority = 'normal', activate }: EnqueueInput) => {
      setQueue((prev) => {
        if (active?.id === id || prev.some((item) => item.id === id)) {
          return prev;
        }
        return [
          ...prev,
          {
            id,
            priority,
            createdAt: Date.now(),
            activate,
          },
        ];
      });
    },
    [active?.id],
  );

  const cancel = useCallback((id: string) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const release = useCallback(
    (id: string) => {
      setQueue((prev) => prev.filter((item) => item.id !== id));
      setActive((current) => (current?.id === id ? null : current));
    },
    [setQueue, setActive],
  );

  useEffect(() => {
    if (active || queue.length === 0) return;

    const next = [...queue].sort(
      (a, b) => PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority] || a.createdAt - b.createdAt,
    )[0];

    if (!next) return;

    setQueue((prev) => prev.filter((item) => item.id !== next.id));
    setActive(next);
    next.activate();
  }, [active, queue]);

  const isActive = useCallback((id: string) => active?.id === id, [active?.id]);

  const activeId = active?.id ?? null;
  const activePriority = active?.priority ?? null;

  const hasBlocking = useMemo(() => {
    if (!activePriority) return false;
    return PRIORITY_RANK[activePriority] >= PRIORITY_RANK.high;
  }, [activePriority]);

  const value = useMemo(
    () => ({
      enqueue,
      release,
      cancel,
      isActive,
      activeId,
      activePriority,
      hasBlocking,
    }),
    [enqueue, release, cancel, isActive, activeId, activePriority, hasBlocking],
  );

  return (
    <NotificationQueueContext.Provider value={value}>{children}</NotificationQueueContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotificationQueue(): NotificationQueueContextType {
  const context = useContext(NotificationQueueContext);
  if (!context) {
    throw new Error('useNotificationQueue must be used within a NotificationQueueProvider');
  }
  return context;
}
